import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

const ZebraPrinterWidget = forwardRef(({ templateData, dpi = 203 }, ref) => {
  const [printer, setPrinter] = useState(null);
  const [status, setStatus] = useState("initializing");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!window.BrowserPrint) {
      setStatus("BrowserPrint not loaded");
      return;
    }
    window.BrowserPrint.getDefaultDevice(
      "printer",
      (device) => {
        if (device) {
          setPrinter(device);
          setStatus(`Ready: ${device.name}`);
        } else {
          setStatus("No default printer found");
        }
      },
      (err) => {
        setError(String(err));
        setStatus("Error detecting printer");
      }
    );
  }, []);

  const onPrint = (copies = 1) => {
    if (!printer) return setStatus("No printer selected");
    if (
      !templateData?.content ||
      !templateData?.printData ||
      !templateData?.dataList
    ) {
      return setStatus("Template data missing");
    }

    const zpl = buildZPLFromTemplate({
      dpi,
      content: templateData.content,
      printData: templateData.printData,
      dataList: templateData.dataList,
      copies,
    });

    printer.send(
      zpl,
      () => setStatus("Printed"),
      (e) => {
        setError(String(e));
        setStatus("Print error");
      }
    );
  };

  useImperativeHandle(ref, () => ({
    onPrint,
  }));

  return <></>;
});

export default ZebraPrinterWidget;

// === Utility functions ===
function stripHtmlTags(str) {
  if (!str) return "";
  return String(str).replace(/<\/?[^>]+(>|$)/g, "");
}

function sanitize(s) {
  const noHtml = stripHtmlTags(s);
  return String(noHtml || "").replace(/[^\x20-\x7E]/g, "");
}

function formatDate(isoOrDate) {
  const d = new Date(isoOrDate);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function wrapTextByWords(text, maxLen = 20) {
  if (!text) return [];
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    // If adding this word exceeds maxLen, push current line and start new
    if ((current + " " + word).trim().length > maxLen) {
      if (current) lines.push(current.trim());
      current = word;
    } else {
      current += (current ? " " : "") + word;
    }
  });

  if (current) lines.push(current.trim());
  return lines;
}

function buildSingleLabel({ x, product, flags }) {
  const blocks = [];
  const fontHead = "^A0N,40,40"; // bigger font
  const fontNorm = "^A0N,26,26";
  const fontSmall = "^A0N,24,24";

  let cy = 180; // start Y position

  if (flags.ItemName && product?.Name) {
    const name = sanitize(product.Name);

    // Word-aware wrapping
    const lines = wrapTextByWords(name, 20);

    lines.forEach((line) => {
      blocks.push(`^FO${x},${cy}${fontHead}^FD${line}^FS`);
      cy += 50; // spacing for next line
    });
  }

  if (flags.Weight && product?.Weight) {
    blocks.push(
      `^FO${x},${cy}${fontNorm}^FDNet Weight: ${sanitize(
        product.Weight
      )} ${sanitize(product.Unit)}^FS`
    );
    cy += 30;
  }

  if (flags.ManufacturingDate && product?.MfgDate) {
    blocks.push(
      `^FO${x},${cy}${fontNorm}^FDPacked on: ${formatDate(product.MfgDate)}^FS`
    );
    cy += 30;
  }

  if (flags.ExpiryDate && product?.ExpiryDate) {
    blocks.push(
      `^FO${x},${cy}${fontNorm}^FDBest Before: ${formatDate(
        product.ExpiryDate
      )}^FS`
    );
    cy += 30;
  }

  if (flags.BatchNo && product?.BatchNo) {
    blocks.push(
      `^FO${x},${cy}${fontNorm}^FDBatch No: ${sanitize(product.BatchNo)}^FS`
    );
    cy += 30;
  }

  if (flags.Mrp && product?.Mrp) {
    blocks.push(`^FO${x},${cy}${fontNorm}^FDMRP: ${sanitize(product.Mrp)}^FS`);
    cy += 30;
  }

  if (flags.Rate && product?.Rate) {
    blocks.push(
      `^FO${x},${cy}${fontNorm}^FDRate: ${sanitize(product.Rate)}^FS`
    );
    cy += 30;
  }

  if (flags.USP && product?.USP) {
    blocks.push(`^FO${x},${cy}${fontSmall}^FDUSP: ${sanitize(product.USP)}^FS`);
    cy += 64;
  }

  if (flags.Barcode && product?.Barcode) {
    blocks.push(`^FO${x},${cy}^BY2`);
    blocks.push("^BCN,50,Y,N,N");
    blocks.push(`^FD${sanitize(product.Barcode)}^FS`);
  }

  return blocks.join("\n");
}

export function buildZPLFromTemplate({
  dpi = 203,
  content,
  printData,
  dataList,
  copies = 1,
}) {
  const z = [];

  printData.forEach((product) => {
    z.push("^XA");
    z.push("^PW830");
    z.push("^LL560");
    z.push("^LH0,0");
    z.push("^FWN"); // force normal orientation
    z.push(`^PQ${copies}`);

    // Left label at X=40
    z.push(buildSingleLabel({ x: 40, product, flags: dataList }));

    // Right label at X=463
    z.push(buildSingleLabel({ x: 463, product, flags: dataList }));

    z.push("^XZ");
  });

  return z.join("\n");
}
